import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MachineryQuery } from '../api/client'

/**
 * The machinery register's filters, held in the URL.
 *
 * Same reasoning as the vehicle register's own hook: Back restores the exact view
 * a person was looking at, and a filtered register is a link they can send to a
 * colleague. A filtered list nobody can share is half a feature.
 *
 * ⚠️ Every field this reads is also WRITTEN back. `useGridQueryParams` reads
 * `expiryFrom`/`expiryTo` and never writes them, so any `setQuery` call there
 * silently drops an expiry drill-down. Do not reproduce that here — if a field is
 * added to the read side, add it to the write side in the same edit.
 */
const DEFAULT_PAGE_SIZE = 40

function num(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Tri-state: absent is "either", which is not the same as false. */
function bool(value: string | null): boolean | undefined {
  if (value === null) return undefined
  return value === '1' || value === 'true'
}

export type SetMachineryQuery = (
  next: MachineryQuery | ((current: MachineryQuery) => MachineryQuery),
) => void

export function useMachineryQueryParams(): [MachineryQuery, SetMachineryQuery] {
  const [params, setParams] = useSearchParams()

  const query = useMemo<MachineryQuery>(
    () => ({
      page: num(params.get('page')) ?? 1,
      pageSize: num(params.get('pageSize')) ?? DEFAULT_PAGE_SIZE,
      search: params.get('search') ?? undefined,
      machineryCategoryId: num(params.get('categoryId')),
      siteName: params.get('site') ?? undefined,
      make: params.get('make') ?? undefined,
      yearFrom: num(params.get('yearFrom')),
      yearTo: num(params.get('yearTo')),
      hasPlate: bool(params.get('hasPlate')),
      needsAttention: bool(params.get('needsAttention')),
      sortBy: params.get('sortBy') ?? 'code',
      sortDescending: params.get('sortDescending') === '1',
    }),
    [params],
  )

  const setQuery = useCallback<SetMachineryQuery>(
    (next) => {
      const value = typeof next === 'function' ? next(query) : next
      const out = new URLSearchParams()

      // Defaults are omitted, so a clean view has a clean URL.
      if (value.page && value.page !== 1) out.set('page', String(value.page))
      if (value.pageSize && value.pageSize !== DEFAULT_PAGE_SIZE) {
        out.set('pageSize', String(value.pageSize))
      }
      if (value.search) out.set('search', value.search)
      if (value.machineryCategoryId) out.set('categoryId', String(value.machineryCategoryId))
      if (value.siteName) out.set('site', value.siteName)
      if (value.make) out.set('make', value.make)
      if (value.yearFrom) out.set('yearFrom', String(value.yearFrom))
      if (value.yearTo) out.set('yearTo', String(value.yearTo))
      if (value.hasPlate !== undefined) out.set('hasPlate', value.hasPlate ? '1' : '0')
      if (value.needsAttention !== undefined) {
        out.set('needsAttention', value.needsAttention ? '1' : '0')
      }
      if (value.sortBy && value.sortBy !== 'code') out.set('sortBy', value.sortBy)
      if (value.sortDescending) out.set('sortDescending', '1')

      // replace, not push: a filter change is a refinement of where you already
      // are, and stacking each one makes Back walk out one filter at a time.
      setParams(out, { replace: true })
    },
    [query, setParams],
  )

  return [query, setQuery]
}
